import { jsx } from "react/jsx-runtime";
import { createTheme } from "@mui/material";
import { buttonMuiThemeOverride } from "./buttonMuiThemeOverride.js";
import { colorTokens } from "./colorTokens.js";
import { alpha } from "./utils.js";
import { paletteColorsV3, paletteColors } from "./vivicaUiKitColors.js";
import SvgIconCheckboxChecked from "../images/icons/icon_checkbox_checked.svg.js";
import SvgIconCheckboxUnchecked from "../images/icons/icon_checkbox_unchecked.svg.js";
import { commonSizesByElement } from "./dimentions.js";
const { palette } = createTheme(), { augmentColor } = palette, createColor = (mainColor) => augmentColor({ color: { main: mainColor } }), PROJECT_FONT_FAMILY = "Raleway, RalewayLocal, sans-serif", themeOptions = {
  palette: {
    // these are just changed mui colors - had already existed in the system
    primary: {
      main: paletteColorsV3.primary[900],
      dark: paletteColorsV3.primary[1e3],
      light: paletteColorsV3.primary[800],
      ...paletteColorsV3.primary
    },
    secondary: {
      main: paletteColorsV3.secondary[900],
      dark: paletteColorsV3.secondary[1e3],
      light: paletteColorsV3.secondary[800],
      ...paletteColorsV3.secondary
    },
    background: {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      default: paletteColors.blue[100],
      paper: ""
    },
    success: {
      main: paletteColorsV3.tiffanyBlue[900],
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      100: paletteColors.success[100],
      200: paletteColorsV3.tiffanyBlue[900]
    },
    warning: {
      main: paletteColorsV3.sunnyYellow[900],
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      100: paletteColors.warning[100],
      200: paletteColorsV3.sunnyYellow[900]
    },
    error: {
      main: paletteColorsV3.radicalRed[900],
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      100: paletteColors.error[100],
      200: paletteColorsV3.radicalRed[900]
    },
    white: createColor(paletteColorsV3.white),
    black: createColor(paletteColorsV3.grey[800]),
    alert: {
      success: paletteColorsV3.tiffanyBlue[900],
      warning: paletteColorsV3.sunnyYellow[900],
      error: paletteColorsV3.radicalRed[900],
      info: paletteColorsV3.blueGreen[900],
      progress: paletteColorsV3.ube[900],
      achieved: paletteColorsV3.mountbattenPink[900],
      blocked: paletteColorsV3.grey[300],
      focus: paletteColorsV3.mountbattenPink[900]
    },
    grey: paletteColorsV3.grey,
    // the colors which were added accordingly to color scheme changes
    radicalRed: paletteColorsV3.radicalRed,
    sunnyYellow: paletteColorsV3.sunnyYellow,
    tiffanyBlue: paletteColorsV3.tiffanyBlue,
    blueGreen: paletteColorsV3.blueGreen,
    ube: paletteColorsV3.ube,
    gamboge: paletteColorsV3.gamboge,
    mountbattenPink: paletteColorsV3.mountbattenPink,
    ...colorTokens,
    /**
     * @deprecated - do not use it please
     */
    "primary-light": createColor(paletteColorsV3.primary[200]),
    "secondary-light": createColor(paletteColorsV3.secondary[200]),
    icon: {
      darkGrey: paletteColorsV3.grey[800],
      grey: paletteColorsV3.grey[500],
      primary: paletteColorsV3.primary[900],
      secondary: paletteColorsV3.secondary[900],
      white: paletteColorsV3.white
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920
    }
  },
  boxShadows: {
    toggle: `0 1px 2px 0.5px ${alpha("black", 15)}`,
    floatingActionButton: `0px 4px 8px 0px ${alpha("black", 10)}`,
    dropdown: "0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)",
    tooltip: "2px 2px 6px 1px #0000001A, -2px -2px 6px 1px #0000001A",
    cursor: "0px 1px 0.8px 0px rgba(0, 0, 0, 0.5)",
    handle: "0px 2px 2px 0px rgba(0, 0, 0, 0.2), 0px 0px 1px 0px rgba(0, 0, 0, 0.1)",
    dragAndDrop: "2px 2px 6px 1px rgba(0, 0, 0, 0.1), -2px -2px 6px 1px rgba(0, 0, 0, 0.1)",
    overlay: "0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px -1px 6px -4px rgba(0, 0, 0, 0.12)"
  },
  spacing: 1,
  zIndex: {
    // https://mui.com/material-ui/customization/z-index/
    // so, above everything else but tooltips
    dropdown: 1450
  },
  shape: {
    borderRadius: 0
  },
  typography: {
    allVariants: {
      color: colorTokens.content.base.default,
      fontFamily: PROJECT_FONT_FAMILY
    },
    fontFamily: PROJECT_FONT_FAMILY,
    body1: {
      fontSize: "20px",
      fontWeight: 400
    },
    body2: {
      fontSize: "18px",
      fontWeight: 400
    },
    "h1/desktop": {
      fontSize: 32,
      lineHeight: "42px",
      fontWeight: 700
    },
    "h1/tablet": {
      fontSize: 24,
      lineHeight: "36px",
      fontWeight: 700
    },
    "h1/mobile": {
      fontSize: 20,
      lineHeight: "24px",
      fontWeight: 700
    },
    "h2/desktop": {
      fontSize: 24,
      lineHeight: "30px",
      fontWeight: 500
    },
    "h2/tablet": {
      fontSize: 22,
      lineHeight: "30px",
      fontWeight: 500
    },
    "h2/mobile": {
      fontSize: 20,
      lineHeight: "24px",
      fontWeight: 500
    },
    "h3/desktop": {
      fontSize: 20,
      lineHeight: "30px",
      fontWeight: 700
    },
    "h3/tablet": {
      fontSize: 18,
      lineHeight: "30px",
      fontWeight: 700
    },
    "h3/mobile": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 700
    },
    "h4/desktop": {
      fontSize: 18,
      lineHeight: "30px",
      fontWeight: 500
    },
    "h4/tablet": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 500
    },
    "h4/mobile": {
      fontSize: 14,
      lineHeight: "18px",
      fontWeight: 500
    },
    "body/lg": {
      fontSize: 16,
      lineHeight: "28px",
      fontWeight: 500
    },
    "body/md": {
      fontSize: 14,
      lineHeight: "24px",
      fontWeight: 500
    },
    "body/sm": {
      fontSize: 12,
      lineHeight: "22px",
      fontWeight: 500
    },
    "string/xl": {
      fontSize: 18,
      lineHeight: "30px",
      fontWeight: 500
    },
    "string/lg": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 500
    },
    "string/md": {
      fontSize: 14,
      lineHeight: "18px",
      fontWeight: 500
    },
    "string/sm": {
      fontSize: 12,
      lineHeight: "18px",
      fontWeight: 500
    },
    "label/lg": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 700
    },
    "label/md": {
      fontSize: 14,
      lineHeight: "18px",
      fontWeight: 700
    },
    "label/sm": {
      fontSize: 12,
      lineHeight: "18px",
      fontWeight: 700
    },
    "link/xl": {
      fontSize: 18,
      lineHeight: "30px",
      fontWeight: 600
    },
    "link/lg": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 600
    },
    "link/md": {
      fontSize: 14,
      lineHeight: "18px",
      fontWeight: 600
    },
    "link/sm": {
      fontSize: 12,
      lineHeight: "18px",
      fontWeight: 600
    },
    "optional/lg": {
      fontSize: 16,
      lineHeight: "24px",
      fontWeight: 500,
      fontStyle: "italic"
    },
    "optional/md": {
      fontSize: 14,
      lineHeight: "18px",
      fontWeight: 500,
      fontStyle: "italic"
    },
    "optional/sm": {
      fontSize: 12,
      lineHeight: "18px",
      fontWeight: 500,
      fontStyle: "italic"
    },
    "tag/xxs": {
      fontSize: 10,
      lineHeight: "18px",
      fontWeight: 700
    },
    service: {
      fontSize: 10,
      lineHeight: "12px",
      fontWeight: 500
    },
    "10_14_500": {
      fontSize: "10px",
      lineHeight: "14px",
      fontWeight: 500,
      display: "inline-block"
    },
    "10_14_700": {
      fontSize: "10px",
      lineHeight: "14px",
      fontWeight: 700,
      display: "inline-block"
    },
    "10_18_700": {
      fontSize: "10px",
      lineHeight: "18px",
      fontWeight: 700,
      display: "inline-block"
    },
    "11_11_900": {
      fontSize: "11px",
      lineHeight: "11px",
      fontWeight: 900,
      display: "inline-block"
    },
    "12_16_500": {
      fontSize: "12px",
      lineHeight: "16px",
      fontWeight: 500,
      display: "inline-block"
    },
    "12_16_700": {
      fontSize: "12px",
      lineHeight: "16px",
      fontWeight: 700,
      display: "inline-block"
    },
    "12_18_500": {
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 500,
      display: "inline-block"
    },
    "12_18_600": {
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 600,
      display: "inline-block"
    },
    "12_18_700": {
      fontSize: "12px",
      lineHeight: "18px",
      fontWeight: 700,
      display: "inline-block"
    },
    "12_22_500": {
      fontSize: "12px",
      lineHeight: "22px",
      fontWeight: 500,
      display: "inline-block"
    },
    "14_20_400": {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 400,
      display: "inline-block"
    },
    "14_20_500": {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 500,
      display: "inline-block"
    },
    "14_18_500": {
      fontSize: "14px",
      lineHeight: "18px",
      fontWeight: 500,
      display: "inline-block"
    },
    "14_18_600": {
      fontSize: "14px",
      lineHeight: "18px",
      fontWeight: 600,
      display: "inline-block"
    },
    "14_18_700": {
      fontSize: "14px",
      lineHeight: "18px",
      fontWeight: 700,
      display: "inline-block"
    },
    "14_20_700": {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 700,
      display: "inline-block"
    },
    "16_20_500": {
      fontSize: "16px",
      lineHeight: "20px",
      fontWeight: 500,
      display: "inline-block"
    },
    "16_20_400": {
      fontSize: "16px",
      lineHeight: "20px",
      fontWeight: 400,
      display: "inline-block"
    },
    "16_20_600": {
      fontSize: "16px",
      lineHeight: "20px",
      fontWeight: 600,
      display: "inline-block"
    },
    "16_20_700": {
      fontSize: "16px",
      lineHeight: "20px",
      fontWeight: 700,
      display: "inline-block"
    },
    "16_24_600": {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 600,
      display: "inline-block"
    },
    "16_24_400": {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 400,
      display: "inline-block"
    },
    "16_24_500": {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 500,
      display: "inline-block"
    },
    "16_24_700": {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 700,
      display: "inline-block"
    },
    "18_24_700": {
      fontSize: "18px",
      lineHeight: "24px",
      fontWeight: 700,
      display: "inline-block"
    },
    "18_24_500": {
      fontSize: "18px",
      lineHeight: "24px",
      fontWeight: 500,
      display: "inline-block"
    },
    "18_30_600": {
      fontSize: "18px",
      lineHeight: "30px",
      fontWeight: 600,
      display: "inline-block"
    },
    "20_24_700": {
      fontSize: "20px",
      lineHeight: "24px",
      fontWeight: 700,
      display: "inline-block"
    },
    "22_26_500": {
      fontSize: "22px",
      lineHeight: "26px",
      fontWeight: 500,
      display: "inline-block"
    },
    "24_32_700": {
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 700,
      display: "inline-block"
    },
    "24_34_700": {
      fontSize: "24px",
      lineHeight: "34px",
      fontWeight: 700,
      display: "inline-block"
    },
    "32_36_700": {
      fontSize: "32px",
      lineHeight: "36px",
      fontWeight: 700,
      display: "inline-block"
    },
    "32_40_700": {
      fontSize: "32px",
      lineHeight: "40px",
      fontWeight: 700,
      display: "inline-block"
    },
    "36_38_700": {
      fontSize: "36px",
      lineHeight: "38px",
      fontWeight: 700,
      display: "inline-block"
    }
  },
  components: {
    MuiButton: buttonMuiThemeOverride,
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            background: "transparent"
          }
        }
      }
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: !0
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:focus-visible": {
            boxShadow: `inset 0 0 0 1px ${theme.palette.grey[300]} !important`
          }
        })
      },
      defaultProps: {
        style: { fontFamily: "inherit" },
        disableRipple: !0
      }
    },
    MuiPopover: {
      defaultProps: {
        PaperProps: {
          sx: {
            filter: "drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.15));",
            boxShadow: "none"
          }
        },
        TransitionProps: { timeout: 0 }
      },
      styleOverrides: {
        root: {
          ".MuiBackdrop-root": {
            opacity: "0!important"
          }
        }
      }
    },
    MuiBackdrop: {
      defaultProps: {
        sx(theme) {
          return {
            backgroundColor: theme.palette.grey[800],
            opacity: "0.5 !important"
          };
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: paletteColorsV3.white,
          transition: "none!important"
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&:focus": {
            background: "none"
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: () => ({
          boxSizing: "border-box",
          backgroundColor: paletteColorsV3.white,
          borderRadius: 0,
          "@media(hover: hover)": {
            "&:hover": {
              backgroundColor: paletteColorsV3.white
            }
          },
          "&.Mui-focused": {
            backgroundColor: paletteColorsV3.white
          },
          "&.Mui-disabled": {
            background: "none"
          },
          "& > div": {
            padding: "6px 0 6px 0"
          },
          "& > div > div ": {
            color: "#abb3bd"
          },
          "& .MuiInputBase-root ": {
            height: 56
          }
        })
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: paletteColorsV3.grey[800],
          margin: 0,
          ...ownerState.labelPlacement === "top" && {
            alignItems: "flex-start"
          }
        }),
        label: () => ({
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "20px"
        })
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: "14px",
          input: {
            color: theme.palette.text.primary,
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              WebkitTextFillColor: theme.palette.text.disabled
            }
          }
        })
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: "0 2px 0 0" },
        listbox: () => ({
          padding: 0,
          border: "1px solid #DEE9ED",
          maxHeight: "200px",
          overflow: "auto",
          "& > li": {
            fontSize: "14px",
            fontWeight: 400,
            "&.MuiAutocomplete-option": {
              paddingTop: 0,
              paddingBottom: 0
            },
            '&.MuiAutocomplete-option[aria-selected="true"]': {
              background: "none"
            },
            '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused,&.MuiAutocomplete-option:hover': {
              color: paletteColorsV3.secondary[900],
              background: "#EEF5F8 "
            }
          }
        })
      }
    },
    MuiCheckbox: {
      defaultProps: {
        icon: /* @__PURE__ */ jsx(SvgIconCheckboxUnchecked, {}),
        checkedIcon: /* @__PURE__ */ jsx(SvgIconCheckboxChecked, {})
      },
      styleOverrides: {
        root: {
          "&:hover": {
            background: "none"
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          "*": {
            fontSize: "14px!important"
          },
          minHeight: "40px!important",
          "@media(hover:hover)": {
            "&:hover": {
              backgroundColor: theme.palette.secondary[200],
              color: theme.palette.secondary.main
            }
          },
          "&.Mui-focusVisible": {
            backgroundColor: theme.palette.secondary[200],
            color: theme.palette.secondary.main,
            outline: "none"
          },
          "&.Mui-selected, &.Mui-focused": {
            backgroundColor: theme.palette.secondary[300],
            color: theme.palette.secondary.main,
            "@media(hover:hover)": {
              "&:hover": {
                backgroundColor: theme.palette.secondary[200],
                color: theme.palette.secondary.main
              }
            },
            "&.Mui-focusVisible": {
              background: theme.palette.secondary[200]
            }
          }
        })
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          zIndex: t.zIndex.dropdown
        })
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderColor: t.palette.border.base.elevation1
        })
      }
    }
  },
  sizes: commonSizesByElement
}, muiTheme = createTheme(themeOptions);
muiTheme.typography.h1 = {
  [muiTheme.breakpoints.up("xs")]: themeOptions.typography["h1/mobile"],
  [muiTheme.breakpoints.up("sm")]: themeOptions.typography["h1/tablet"],
  [muiTheme.breakpoints.up("lg")]: themeOptions.typography["h1/desktop"]
};
muiTheme.typography.h2 = {
  [muiTheme.breakpoints.up("xs")]: themeOptions.typography["h2/mobile"],
  [muiTheme.breakpoints.up("sm")]: themeOptions.typography["h2/tablet"],
  [muiTheme.breakpoints.up("lg")]: themeOptions.typography["h2/desktop"]
};
muiTheme.typography.h3 = {
  [muiTheme.breakpoints.up("xs")]: themeOptions.typography["h3/mobile"],
  [muiTheme.breakpoints.up("sm")]: themeOptions.typography["h3/tablet"],
  [muiTheme.breakpoints.up("lg")]: themeOptions.typography["h3/desktop"]
};
muiTheme.typography.h4 = {
  [muiTheme.breakpoints.up("xs")]: themeOptions.typography["h4/mobile"],
  [muiTheme.breakpoints.up("sm")]: themeOptions.typography["h4/tablet"],
  [muiTheme.breakpoints.up("lg")]: themeOptions.typography["h4/desktop"]
};
export {
  PROJECT_FONT_FAMILY,
  muiTheme,
  themeOptions
};
//# sourceMappingURL=theme.js.map
